import { Language } from '../types';

export interface RegionalCommuneItem {
  name: Record<Language, string>;
  postalCode: string;
  citySlug?: string; // If dedicated city page exists (e.g. 'bruxelles', 'ixelles', 'sint-pieters-leeuw', etc.)
  avgResponseTime: string;
}

export interface RegionalZoneFaq {
  question: Record<Language, string>;
  answer: Record<Language, string>;
}

export interface RegionalZoneData {
  slug: string;
  name: Record<Language, string>;
  tagline: Record<Language, string>;
  leadText: Record<Language, string>;
  responseTime: string;
  badge: Record<Language, string>;
  regulations: {
    title: Record<Language, string>;
    desc: Record<Language, string>;
    badge: string;
  };
  communes: RegionalCommuneItem[];
  keyHighlights: Record<Language, string[]>;
  faqs: RegionalZoneFaq[];
  testimonial: {
    author: string;
    city: string;
    text: Record<Language, string>;
    rating: number;
    service: Record<Language, string>;
  };
}

export const REGIONAL_ZONES_DATA: RegionalZoneData[] = [
  {
    slug: 'bruxelles-19-communes',
    name: {
      fr: 'Bruxelles-Capitale (Les 19 Communes)',
      nl: 'Brussels Hoofdstedelijk Gewest (De 19 Gemeenten)',
      en: 'Brussels-Capital Region (All 19 Municipalities)'
    },
    tagline: {
      fr: 'Dépannage d\'Urgence Plomberie, Chauffage & Débouchage 24/7 dans tout Bruxelles',
      nl: 'Spoeddienst Loodgieterij, CV-Ketels & Ontstoppingsdienst 24/7 in heel Brussel',
      en: '24/7 Emergency Plumbing, Heating & Drain Unblocking across Brussels'
    },
    leadText: {
      fr: 'Nos techniciens chauffagistes et plombiers qualifiés patrouillent en continu dans les 19 communes bruxelloises. Nous garantissons une arrivée en 20 à 30 minutes avec des véhicules équipés pour régler votre panne sur-le-champ.',
      nl: 'Onze erkende loodgieters en verwarmingsmonteurs zijn permanent mobiel in alle 19 Brusselse gemeenten. Wij garanderen een aankomst binnen 20 tot 30 minuten met volledig uitgeruste wagens.',
      en: 'Our certified mobile technicians operate 24/7 across all 19 Brussels municipalities with guaranteed 20-30 min arrival times and complete on-board equipment.'
    },
    responseTime: '20 - 30 min',
    badge: {
      fr: '19 Communes • 24/7 Express',
      nl: '19 Gemeenten • 24/7 Express',
      en: '19 Municipalities • 24/7 Express'
    },
    regulations: {
      title: {
        fr: 'Réglementation PEB & Habilitation Bruxelles Environnement',
        nl: 'EPB-Regelgeving & Erkenning Leefmilieu Brussel',
        en: 'EPB Compliance & Brussels Environment Certified'
      },
      desc: {
        fr: 'À Bruxelles, le contrôle périodique des chaudières au gaz est légalement obligatoire tous les 2 ans. Nos techniciens agréés G1/G2 délivrent l\'attestation officielle de contrôle et de conformité exigée par la Région.',
        nl: 'In Brussel is het periodiek onderhoud van gasketels wettelijk verplicht om de 2 jaar. Onze G1/G2 erkende technici leveren het officiële keuringsattest af vereist door het Gewest.',
        en: 'In Brussels, periodic gas boiler servicing is legally mandatory every 2 years. Our G1/G2 certified engineers provide official certificates compliant with regional regulations.'
      },
      badge: 'G1 / G2 Agréé Région Bruxelles'
    },
    communes: [
      { name: { fr: 'Bruxelles-Ville (Pentagone, Louise, Laeken)', nl: 'Brussel-Stad (Vijfhoek, Louiza, Laken)', en: 'Brussels City (Pentagon, Louise, Laeken)' }, postalCode: '1000', citySlug: 'bruxelles', avgResponseTime: '20 min' },
      { name: { fr: 'Schaerbeek', nl: 'Schaarbeek', en: 'Schaerbeek' }, postalCode: '1030', avgResponseTime: '25 min' },
      { name: { fr: 'Etterbeek (Jourdan, Cinquantenaire)', nl: 'Etterbeek (Jourdan, Jubelpark)', en: 'Etterbeek (Jourdan, Cinquantenaire)' }, postalCode: '1040', avgResponseTime: '25 min' },
      { name: { fr: 'Ixelles (Flagey, Châtelain, ULB)', nl: 'Elsene (Flagey, Kastelein, VUB)', en: 'Ixelles (Flagey, Chatelain, ULB)' }, postalCode: '1050', citySlug: 'ixelles', avgResponseTime: '20 min' },
      { name: { fr: 'Saint-Gilles (Parvis, Barrière)', nl: 'Sint-Gillis (Voorplein, Bareel)', en: 'Saint-Gilles (Parvis, Barrier)' }, postalCode: '1060', avgResponseTime: '20 min' },
      { name: { fr: 'Anderlecht (Cureghem, Saint-Guidon)', nl: 'Anderlecht (Kuregem, Sint-Guido)', en: 'Anderlecht (Cureghem, Saint-Guidon)' }, postalCode: '1070', citySlug: 'anderlecht', avgResponseTime: '15 min' },
      { name: { fr: 'Molenbeek-Saint-Jean', nl: 'Sint-Jans-Molenbeek', en: 'Molenbeek-Saint-Jean' }, postalCode: '1080', avgResponseTime: '25 min' },
      { name: { fr: 'Koekelberg (Basilique)', nl: 'Koekelberg (Basiliek)', en: 'Koekelberg (Basilica)' }, postalCode: '1081', avgResponseTime: '25 min' },
      { name: { fr: 'Berchem-Sainte-Agathe', nl: 'Sint-Agatha-Berchem', en: 'Berchem-Sainte-Agathe' }, postalCode: '1082', avgResponseTime: '25 min' },
      { name: { fr: 'Ganshoren', nl: 'Ganshoren', en: 'Ganshoren' }, postalCode: '1083', avgResponseTime: '25 min' },
      { name: { fr: 'Jette (Miroir)', nl: 'Jette (Spiegelplein)', en: 'Jette (Mirror Sq)' }, postalCode: '1090', avgResponseTime: '25 min' },
      { name: { fr: 'Evere', nl: 'Evere', en: 'Evere' }, postalCode: '1140', avgResponseTime: '25 min' },
      { name: { fr: 'Woluwe-Saint-Pierre (Montgomery)', nl: 'Sint-Pieters-Woluwe (Montgomery)', en: 'Woluwe-Saint-Pierre (Montgomery)' }, postalCode: '1150', avgResponseTime: '30 min' },
      { name: { fr: 'Auderghem (Herrmann-Debroux)', nl: 'Oudergem (Herrmann-Debroux)', en: 'Auderghem (Herrmann-Debroux)' }, postalCode: '1160', avgResponseTime: '25 min' },
      { name: { fr: 'Watermael-Boitsfort', nl: 'Watermaal-Bosvoorde', en: 'Watermael-Boitsfort' }, postalCode: '1170', avgResponseTime: '25 min' },
      { name: { fr: 'Uccle (Fort Jaco, Churchill, Saint-Job)', nl: 'Ukkel (Fort Jaco, Churchill, Sint-Job)', en: 'Uccle (Fort Jaco, Churchill, Saint-Job)' }, postalCode: '1180', citySlug: 'uccle', avgResponseTime: '20 min' },
      { name: { fr: 'Forest (Altitude 100, Wiels)', nl: 'Vorst (Hoogte 100, Wiels)', en: 'Forest (Altitude 100, Wiels)' }, postalCode: '1190', avgResponseTime: '20 min' },
      { name: { fr: 'Woluwe-Saint-Lambert (Georges Henri)', nl: 'Sint-Lambrechts-Woluwe', en: 'Woluwe-Saint-Lambert' }, postalCode: '1200', avgResponseTime: '30 min' },
      { name: { fr: 'Saint-Josse-ten-Noode (Madou)', nl: 'Sint-Joost-ten-Node (Madou)', en: 'Saint-Josse-ten-Noode (Madou)' }, postalCode: '1210', avgResponseTime: '25 min' }
    ],
    keyHighlights: {
      fr: [
        'Intervention en 20 à 30 minutes garantie dans l\'ensemble des 19 communes',
        'Agrément officiel Bruxelles Environnement (G1, G2, attestation légale PEB)',
        'Hydrocureuse haute pression et furets mécaniques adaptés aux vieux immeubles bruxellois',
        'Recherche de fuite non destructive par caméra thermique et gaz traceur',
        'Dépannage d\'urgence 24h/24 et 7j/7, dimanches et jours fériés inclus'
      ],
      nl: [
        'Gegarandeerde interventie binnen 20 tot 30 minuten in alle 19 gemeenten',
        'Officiële erkenning Leefmilieu Brussel (G1, G2, wettelijk EPB-attest)',
        'Hogedruk hydro-jetting en veermachines aangepast aan typisch Brusselse herenhuizen',
        'Niet-destructieve lekdetectie met thermografische camera en traceergas',
        'Dringende pechdienst 24/7, inclusief zon- en feestdagen'
      ],
      en: [
        'Guaranteed 20-30 min emergency arrival across all 19 municipalities',
        'Certified by Brussels Environment (G1, G2, official EPB compliance certificate)',
        'Hydro-jetting and mechanical snakes suited for classic Brussels apartment buildings',
        'Non-destructive leak detection via thermal imaging and acoustic sensors',
        '24/7 round-the-clock emergency support, Sundays and bank holidays included'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Couvrez-vous vraiment les 19 communes de Bruxelles sans supplément de déplacement ?',
          nl: 'Dekken jullie effectief alle 19 Brusselse gemeenten zonder extra verplaatsingskosten?',
          en: 'Do you truly cover all 19 Brussels municipalities without extra travel fees?'
        },
        answer: {
          fr: 'Oui, l\'ensemble de la Région de Bruxelles-Capitale (du Pentagone à Uccle, en passant par Schaerbeek et Woluwe) est desservi à tarif standard transparent fixé à l\'avance.',
          nl: 'Ja, het volledige Brussels Hoofdstedelijk Gewest (van de Vijfhoek tot Ukkel, inclusief Schaarbeek en Woluwe) wordt bediend aan transparante standaardtarieven.',
          en: 'Yes, the entire Brussels-Capital Region is served under transparent standard rates with upfront price confirmations.'
        }
      },
      {
        question: {
          fr: 'Délivrez-vous l\'attestation de contrôle périodique obligatoire à Bruxelles ?',
          nl: 'Leveren jullie het verplichte periodieke keuringsattest af in Brussel?',
          en: 'Do you issue the mandatory periodic boiler inspection certificate for Brussels?'
        },
        answer: {
          fr: 'Absolument. Nos techniciens habilités remettent immédiatement l\'attestation légale de contrôle périodique conforme aux exigences de Bruxelles Environnement.',
          nl: 'Absoluut. Onze erkende technici overhandigen direct het officiële periodieke reinigings- en verbrandingsattest conform de eisen van Leefmilieu Brussel.',
          en: 'Absolutely. Our certified heating engineers issue the official compliance certificate required by Brussels Environment immediately upon service completion.'
        }
      }
    ],
    testimonial: {
      author: 'Alexandre V.',
      city: 'Bruxelles (Quartier Flagey - Ixelles)',
      text: {
        fr: 'Chaudière Vaillant en sécurité un dimanche soir de gel. Équipe arrivée en 25 minutes, pièce remplacée sur place avec attestation officielle. Service irréprochable à Bruxelles.',
        nl: 'Vaillant cv-ketel in storing op een ijskoude zondagavond. Binnen 25 minuten ter plaatse, wisselstuk direct vervangen met officieel attest. Onberispelijke service in Brussel.',
        en: 'Vaillant boiler failed on a freezing Sunday evening. Arrived in 25 minutes, replaced part immediately with official certification. Flawless service.'
      },
      rating: 5,
      service: {
        fr: 'Dépannage d\'urgence Chaudière Gaz',
        nl: 'Spoedherstelling Gasketel',
        en: 'Emergency Gas Boiler Repair'
      }
    }
  },
  {
    slug: 'brabant-flamand',
    name: {
      fr: 'Brabant Flamand & Ring (Vlaams-Brabant & Rand)',
      nl: 'Vlaams-Brabant & Brusselse Rand',
      en: 'Flemish Brabant & Brussels Periphery'
    },
    tagline: {
      fr: 'Dépannage Express en 15-25 min depuis notre Siège à Sint-Pieters-Leeuw',
      nl: 'Express Depannage binnen 15-25 min vanuit onze Hoofdzetel te Sint-Pieters-Leeuw',
      en: 'Express Dispatch in 15-25 min from our Sint-Pieters-Leeuw Headquarters'
    },
    leadText: {
      fr: 'Le siège social et l\'atelier central d\'API HEAT sont implantés au 24/8 Bergensesteenweg à Sint-Pieters-Leeuw. Grâce à cette position stratégique sur le Ring Ouest et Sud, nous offrons les délais d\'arrivée les plus courts de tout le Brabant Flamand (15 à 20 min).',
      nl: 'De maatschappelijke zetel en het centrale atelier van API HEAT zijn gevestigd aan de Bergensesteenweg 24/8 te Sint-Pieters-Leeuw. Dankzij deze strategische ligging aan de West- en Zuidring garanderen wij de snelste aankomsttijden in Vlaams-Brabant (15 tot 20 min).',
      en: 'API HEAT is headquartered at Bergensesteenweg 24/8 in Sint-Pieters-Leeuw. Positioned right by the Ring, we provide the fastest arrival times in Flemish Brabant (15-20 min).'
    },
    responseTime: '15 - 25 min',
    badge: {
      fr: 'Siège Central API HEAT • 15 min Express',
      nl: 'Centrale Hoofdzetel API HEAT • 15 min Express',
      en: 'API HEAT Headquarters • 15 min Express'
    },
    regulations: {
      title: {
        fr: 'Réglementation Flamande & Traitement du Calcaire (Zennevallei)',
        nl: 'Vlaamse Wetgeving & Kalkbehandeling (Zennevallei)',
        en: 'Flemish Regulations & Water Softening'
      },
      desc: {
        fr: 'En Région Flamande, l\'entretien des chaudières au gaz est obligatoire tous les 2 ans et tous les ans pour le mazout. De plus, l\'eau du Brabant Flamand étant particulièrement calcaire (supérieure à 35°fH), nous sommes experts en pose d\'adoucisseurs d\'eau haute efficacité.',
        nl: 'In Vlaanderen is tweejaarlijks onderhoud van gasketels en jaarlijks onderhoud van stookolieketels verplicht. Omdat het leidingwater in Vlaams-Brabant zeer hard is (>35°fH), plaatsen en onderhouden wij krachtige waterontharders.',
        en: 'In Flanders, gas boiler maintenance is required every 2 years and annual for oil. As local water is very hard (>35°fH), we install high-efficiency water softeners.'
      },
      badge: 'Erkend Technicus Vlaamse Overheid'
    },
    communes: [
      { name: { fr: 'Sint-Pieters-Leeuw (Siège Central API HEAT)', nl: 'Sint-Pieters-Leeuw (Hoofdzetel API HEAT)', en: 'Sint-Pieters-Leeuw (API HEAT HQ)' }, postalCode: '1600', citySlug: 'sint-pieters-leeuw', avgResponseTime: '15 min' },
      { name: { fr: 'Halle (Hal, Lembeek, Buizingen)', nl: 'Halle (Lembeek, Buizingen)', en: 'Halle (Lembeek, Buizingen)' }, postalCode: '1500', avgResponseTime: '15 min' },
      { name: { fr: 'Beersel (Lot, Alsemberg, Dworp)', nl: 'Beersel (Lot, Alsemberg, Dworp)', en: 'Beersel (Lot, Alsemberg, Dworp)' }, postalCode: '1650', avgResponseTime: '20 min' },
      { name: { fr: 'Dilbeek (Groot-Bijgaarden, Itterbeek)', nl: 'Dilbeek (Groot-Bijgaarden, Itterbeek)', en: 'Dilbeek (Groot-Bijgaarden, Itterbeek)' }, postalCode: '1700', avgResponseTime: '20 min' },
      { name: { fr: 'Drogenbos', nl: 'Drogenbos', en: 'Drogenbos' }, postalCode: '1620', avgResponseTime: '15 min' },
      { name: { fr: 'Linkebeek', nl: 'Linkebeek', en: 'Linkebeek' }, postalCode: '1630', avgResponseTime: '15 min' },
      { name: { fr: 'Vilvoorde (Vilvorde, Peutie)', nl: 'Vilvoorde (Peutie)', en: 'Vilvoorde (Peutie)' }, postalCode: '1800', avgResponseTime: '25 min' },
      { name: { fr: 'Zaventem (Nossegem, Sint-Stevens-Woluwe)', nl: 'Zaventem (Nossegem, Sint-Stevens-Woluwe)', en: 'Zaventem' }, postalCode: '1930', avgResponseTime: '25 min' },
      { name: { fr: 'Grimbergen (Strombeek-Bever, Humbeek)', nl: 'Grimbergen (Strombeek-Bever, Humbeek)', en: 'Grimbergen' }, postalCode: '1850', avgResponseTime: '25 min' },
      { name: { fr: 'Wemmel', nl: 'Wemmel', en: 'Wemmel' }, postalCode: '1780', avgResponseTime: '25 min' },
      { name: { fr: 'Asse (Zellik, Mollem)', nl: 'Asse (Zellik, Mollem)', en: 'Asse (Zellik, Mollem)' }, postalCode: '1730', avgResponseTime: '25 min' },
      { name: { fr: 'Tervuren (Moorsel, Vossem)', nl: 'Tervuren (Moorsel, Vossem)', en: 'Tervuren' }, postalCode: '3080', avgResponseTime: '25 min' },
      { name: { fr: 'Overijse (Jezus-Eik)', nl: 'Overijse (Jezus-Eik)', en: 'Overijse' }, postalCode: '1900', avgResponseTime: '25 min' },
      { name: { fr: 'Hoeilaart', nl: 'Hoeilaart', en: 'Hoeilaart' }, postalCode: '1560', avgResponseTime: '20 min' },
      { name: { fr: 'Kraainem', nl: 'Kraainem', en: 'Kraainem' }, postalCode: '1950', avgResponseTime: '25 min' },
      { name: { fr: 'Wezembeek-Oppem', nl: 'Wezembeek-Oppem', en: 'Wezembeek-Oppem' }, postalCode: '1970', avgResponseTime: '25 min' },
      { name: { fr: 'Meise (Wolvertem)', nl: 'Meise (Wolvertem)', en: 'Meise (Wolvertem)' }, postalCode: '1860', avgResponseTime: '25 min' },
      { name: { fr: 'Leuven (Louvain, Heverlee, Kessel-Lo)', nl: 'Leuven (Heverlee, Kessel-Lo)', en: 'Leuven (Heverlee, Kessel-Lo)' }, postalCode: '3000', avgResponseTime: '30 min' }
    ],
    keyHighlights: {
      fr: [
        'Proximité immédiate depuis notre siège (15-20 min à Sint-Pieters-Leeuw, Halle, Beersel, Dilbeek)',
        'Techniciens bilingues (FR / NL) certifiés par la Région Flamande (Vlaamse overheid)',
        'Spécialiste de la dureté de l\'eau : pose et dépannage d\'adoucisseurs d\'eau dans toute la périphérie',
        'Équipement d\'hydrocurage pour fosses, égouts et canalisations pavillonnaires',
        'Dépannage d\'urgence 24/7 le long du Ring de Bruxelles'
      ],
      nl: [
        'Onmiddellijke nabijheid vanaf onze zetel (15-20 min in Sint-Pieters-Leeuw, Halle, Beersel, Dilbeek)',
        'Tweetalige technici (NL / FR) erkend door de Vlaamse Overheid met wettelijk reinigingsattest',
        'Specialist in hard water: plaatsing en herstelling van waterontharders in heel de Rand',
        'Hogedruk hydrocureuse voor septische putten, toezichtsputten en rioleringen van woningen',
        '24/7 spoeddienst langs de volledige Brusselse Ring'
      ],
      en: [
        'Direct proximity from our HQ (15-20 min to Sint-Pieters-Leeuw, Halle, Beersel, Dilbeek)',
        'Bilingual technicians certified under Flemish regional standards with official certificates',
        'Water hardness specialists: water softener installations and maintenance across the rim',
        'Hydro-jetting equipment suited for private sewers and septic systems',
        '24/7 emergency dispatch along the entire Brussels Ring road'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Quel est votre délai réel d\'intervention dans le Brabant Flamand Sud (Leeuw, Halle, Beersel) ?',
          nl: 'Wat is jullie reële aankomsttijd in Zuid-Vlaams-Brabant (Leeuw, Halle, Beersel)?',
          en: 'What is your actual response time in South Flemish Brabant?'
        },
        answer: {
          fr: 'Puisque nos bureaux et notre stock de pièces sont sur la Bergensesteenweg à Sint-Pieters-Leeuw, nous arrivons sur place en 15 à 20 minutes chrono.',
          nl: 'Aangezien ons hoofdkantoor en wisselstukkendepot aan de Bergensesteenweg in Leeuw liggen, zijn we binnen 15 tot 20 minuten bij u ter plaatse.',
          en: 'Because our central warehouse and offices are on Bergensesteenweg in Sint-Pieters-Leeuw, we reach your door in 15 to 20 minutes.'
        }
      }
    ],
    testimonial: {
      author: 'Wim C.',
      city: 'Sint-Pieters-Leeuw (Zuun)',
      text: {
        fr: 'Rupture de canalisation d\'eau dans la cave. Arrivée d\'API HEAT en 15 minutes montre en main. Fuite réparée et conseils pour l\'adoucisseur d\'eau. Impeccable.',
        nl: 'Gesprongen waterleiding in de kelder. API HEAT was er binnen 15 minuten. Lek gedicht en professioneel advies voor onze ontkalker. Topservice!',
        en: 'Burst pipe in our basement. API HEAT arrived in 15 minutes flat. Repaired cleanly and gave great advice on our water softener. Top rating.'
      },
      rating: 5,
      service: {
        fr: 'Urgence Fuite d\'Eau & Plomberie',
        nl: 'Spoedherstelling Waterlek',
        en: 'Emergency Water Leak Repair'
      }
    }
  },
  {
    slug: 'brabant-wallon',
    name: {
      fr: 'Brabant Wallon (Waals-Brabant)',
      nl: 'Waals-Brabant (Brabant Wallon)',
      en: 'Walloon Brabant'
    },
    tagline: {
      fr: 'Chauffagiste & Plombier Agréé à Waterloo, Braine-l\'Alleud, Wavre et environs',
      nl: 'Erkend Loodgieter & Verwarmingsmonteur in Waterloo, Eigenbrakel en Waver',
      en: 'Certified Heating Engineer & Plumber in Waterloo, Braine-l\'Alleud, Wavre'
    },
    leadText: {
      fr: 'API HEAT déploie ses techniciens qualifiés sur tout le Brabant Wallon pour villas, résidences et maisons familiales. Nous assurons l\'entretien légal des chaudières au gaz et au mazout ainsi que le débouchage et la plomberie d\'urgence.',
      nl: 'API HEAT stuurt ervaren technici uit over heel Waals-Brabant voor villa\'s, residenties en eengezinswoningen. Wij verzorgen het wettelijk onderhoud van gas- en stookolieketels, alsook sanitair en ontstopping.',
      en: 'API HEAT deploys certified master technicians across Walloon Brabant for family residences and villas. We handle periodic gas and oil boiler servicing and 24/7 emergency unblocking.'
    },
    responseTime: '25 - 35 min',
    badge: {
      fr: 'Brabant Wallon • Intervention 24/7',
      nl: 'Waals-Brabant • 24/7 Interventie',
      en: 'Walloon Brabant • 24/7 Service'
    },
    regulations: {
      title: {
        fr: 'Agrément Région Wallonne (SPW) - Chaudières Gaz & Mazout',
        nl: 'Erkenning Waals Gewest (SPW) - Gas- en Stookolieketels',
        en: 'Walloon Region (SPW) Certified - Gas & Oil Boilers'
      },
      desc: {
        fr: 'En Région Wallonne, le contrôle périodique est obligatoire tous les 2 ou 3 ans selon la puissance pour le gaz, et annuel pour le mazout avec ramonage de cheminée. Nos chauffagistes délivrent l\'attestation de contrôle officielle SPW.',
        nl: 'In het Waals Gewest is een periodieke controle verplicht voor gas (om de 2 à 3 jaar) en jaarlijks voor stookolie inclusief schoorsteenvegen. Wij bezorgen het officiële SPW-attest.',
        en: 'In Wallonia, periodic compliance checks are required for gas and annual for oil with chimney sweeping included. We provide the official SPW certificate.'
      },
      badge: 'Habilité SPW Région Wallonne'
    },
    communes: [
      { name: { fr: 'Waterloo (Mont-Saint-Jean, Chenois, Joli-Bois)', nl: 'Waterloo (Mont-Saint-Jean, Chenois)', en: 'Waterloo (Mont-Saint-Jean, Chenois)' }, postalCode: '1410', citySlug: 'waterloo', avgResponseTime: '25 min' },
      { name: { fr: 'Braine-l\'Alleud (Ophain, Lillois)', nl: 'Eigenbrakel (Ophain, Lillois)', en: 'Braine-l\'Alleud (Ophain, Lillois)' }, postalCode: '1420', avgResponseTime: '25 min' },
      { name: { fr: 'Wavre (Limal, Bierges)', nl: 'Waver (Limal, Bierges)', en: 'Wavre (Limal, Bierges)' }, postalCode: '1300', avgResponseTime: '30 min' },
      { name: { fr: 'Nivelles (Baulers, Thines)', nl: 'Nijvel (Baulers, Thines)', en: 'Nivelles (Baulers, Thines)' }, postalCode: '1400', avgResponseTime: '30 min' },
      { name: { fr: 'Tubize (Clabecq, Saintes)', nl: 'Tubeke (Klabbeek, Sint-Renelde)', en: 'Tubize (Clabecq, Saintes)' }, postalCode: '1480', avgResponseTime: '20 min' },
      { name: { fr: 'Lasne (Ohain, Couture-Saint-Germain)', nl: 'Lasne (Ohain)', en: 'Lasne (Ohain)' }, postalCode: '1380', avgResponseTime: '30 min' },
      { name: { fr: 'Rixensart (Genval, Rosières)', nl: 'Rixensart (Genval, Rosières)', en: 'Rixensart (Genval, Rosières)' }, postalCode: '1330', avgResponseTime: '30 min' },
      { name: { fr: 'Ottignies-Louvain-la-Neuve', nl: 'Ottignies-Louvain-la-Neuve', en: 'Ottignies-Louvain-la-Neuve' }, postalCode: '1340', avgResponseTime: '30 min' },
      { name: { fr: 'Genappe (Baisy-Thy, Vieux-Genappe)', nl: 'Genepiën', en: 'Genappe' }, postalCode: '1470', avgResponseTime: '35 min' },
      { name: { fr: 'La Hulpe (Château)', nl: 'Terhulpen', en: 'La Hulpe' }, postalCode: '1310', avgResponseTime: '25 min' },
      { name: { fr: 'Ittre (Virginal, Haut-Ittre)', nl: 'Itter', en: 'Ittre' }, postalCode: '1460', avgResponseTime: '25 min' },
      { name: { fr: 'Braine-le-Château (Wauthier-Braine)', nl: 'Kasteelbrakel', en: 'Braine-le-Château' }, postalCode: '1440', avgResponseTime: '20 min' }
    ],
    keyHighlights: {
      fr: [
        'Intervention rapide sur l\'axe RO / E19 / E411 en 25 à 35 minutes',
        'Entretien complet des chaudières au mazout et au gaz (Viessmann, Buderus, Vaillant, Bulex)',
        'Dépannage d\'urgence pour fuite d\'eau sous chape, plancher chauffant et sanitaires',
        'Hydrocurage puissant pour canalisations extérieures, chambres de visite et fosses',
        'Attestation légale SPW fournie immédiatement sur place'
      ],
      nl: [
        'Snelle interventie via RO / E19 / E411 binnen 25 tot 35 minuten',
        'Compleet onderhoud van stookolie- en gasketels (Viessmann, Buderus, Vaillant, Bulex)',
        'Spoedherstelling bij waterlek onder de vloer, vloerverwarming en sanitair',
        'Krachtige hogedrukreiniging voor toezichtsputten en buitenrioleringen',
        'Wettelijk SPW-attest direct ter plaatse uitgereikt'
      ],
      en: [
        'Rapid response via RO / E19 / E411 corridors in 25 to 35 minutes',
        'Comprehensive maintenance for oil and gas boilers (Viessmann, Buderus, Vaillant, Bulex)',
        'Emergency detection and repairs for slab leaks and underfloor radiant loops',
        'Heavy-duty hydro-jetting for exterior mains, inspection pits, and septic chambers',
        'Official SPW compliance certificate delivered instantly on-site'
      ]
    },
    faqs: [
      {
        question: {
          fr: 'Prenez-vous en charge les chaudières au mazout dans le Brabant Wallon ?',
          nl: 'Onderhouden jullie ook stookolieketels in Waals-Brabant?',
          en: 'Do you service heating oil boilers in Walloon Brabant?'
        },
        answer: {
          fr: 'Oui, nous entretenons et dépannons toutes les marques de chaudières au mazout (Viessmann, Buderus, ACV, Saint Roch) avec contrôle de combustion et ramonage réglementaire.',
          nl: 'Ja, wij herstellen en onderhouden alle merken stookolieketels met rookgasmeting en schoorsteenreiniging volgens de geldende normen.',
          en: 'Yes, we service and troubleshoot all oil-fired boiler models with flue gas analysis and mandatory chimney cleaning included.'
        }
      }
    ],
    testimonial: {
      author: 'Caroline T.',
      city: 'Waterloo (Faubourg)',
      text: {
        fr: 'Entretien annuel chaudière et détartrage chauffe-eau. Chauffagiste ponctuel, minutieux et très propre. Attestation remise immédiatement par email et papier.',
        nl: 'Jaarlijks ketelonderhoud en ontkalking boiler. Zeer stipt, grondig en proper werk. Attest meteen digitaal en op papier ontvangen.',
        en: 'Annual boiler servicing and descaling. Punctual, meticulous, and left everything clean. Received certificate instantly in print and email.'
      },
      rating: 5,
      service: {
        fr: 'Entretien Chaudière & Chauffe-eau',
        nl: 'Onderhoud Ketel & Boiler',
        en: 'Boiler & Water Heater Service'
      }
    }
  }
];

export function getRegionalZoneBySlug(slug: string): RegionalZoneData | undefined {
  return REGIONAL_ZONES_DATA.find((z) => z.slug.toLowerCase() === slug.toLowerCase());
}
